import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { LogDetail } from '../../../../../../../types/log.interface';
import { tryTranslate } from './i18n.utils';
import { ValueChangeComponent } from './value-change.component';

@Component({
  selector: 'ces-auscultation-detail',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ValueChangeComponent, TranslocoModule],
  templateUrl: './auscultation-detail.component.html',
})
export class AuscultationDetailComponent {
  private readonly translate = inject(TranslocoService);
  private readonly translations = toSignal(this.translate.selectTranslation());
  readonly details = input.required<LogDetail[]>();
  readonly first = computed<LogDetail | undefined>(() => this.details()[0]);

  readonly entityName = computed(() => {
    this.translations();
    const name = this.first()?.entityName;
    if (name) return tryTranslate(this.translate, name);
    return this.translate.translate('history.logContent.auscultationInfo');
  });

  readonly attributeName = computed(() => {
    this.translations();
    return this.getAttributeDisplayName(this.first()?.attribute ?? '');
  });

  readonly truncatedDetail = computed(() => {
    const d = this.first();
    if (!d) return null;
    return {
      ...d,
      ...(d.oldValue !== undefined && { oldValue: this.truncateText(d.oldValue) }),
      ...(d.newValue !== undefined && { newValue: this.truncateText(d.newValue) }),
    };
  });

  readonly isSpecialAttribute = computed(() =>
    ['file', 'image', 'choice_value', 'text_value', 'planned'].includes(this.first()?.attribute ?? ''),
  );

  private getAttributeDisplayName(attribute: string): string {
    const attributeMap: Record<string, string> = {
      choice_value: this.translate.translate('history.logContent.choice'),
      text_value: this.translate.translate('history.logContent.text'),
    };
    return attributeMap[attribute] ?? attribute;
  }

  private truncateText(text: string, maxLength: number = 50): string {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  }
}
